import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WeatherHistoryModal } from "./WeatherHistoryModal";
import { X, Minus, ChevronDown, CloudSun } from "lucide-react";
import { getObraStats } from "@/services/obraStats";

interface WorkChartProps {
  obraId?: string;
  onClose?: () => void;
  onMinimize?: () => void;
  isMinimized?: boolean;
}

export function WorkChart({ obraId, onClose, onMinimize, isMinimized }: WorkChartProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Dias praticáveis/impraticáveis derivados do clima registrado nos RDOs.
  // Um dia conta como impraticável se algum turno foi marcado não-praticável.
  const [counts, setCounts] = useState({ praticaveis: 0, impraticaveis: 0 });
  useEffect(() => {
    let active = true;
    if (!obraId) { setCounts({ praticaveis: 0, impraticaveis: 0 }); return; }
    getObraStats(obraId)
      .then((s) => {
        if (!active) return;
        let praticaveis = 0;
        let impraticaveis = 0;
        for (const rdo of s.rdos) {
          const unworkable =
            rdo.weather?.morning?.workable === false ||
            rdo.weather?.afternoon?.workable === false;
          if (unworkable) impraticaveis++;
          else praticaveis++;
        }
        setCounts({ praticaveis, impraticaveis });
      })
      .catch((e) => console.error("WorkChart:", e));
    return () => { active = false; };
  }, [obraId]);

  const data = [
    { label: "Dias Praticáveis", value: counts.praticaveis, color: "#0071CE" },
    { label: "Dias Impraticáveis", value: counts.impraticaveis, color: "#DC3545" },
  ];

  const total = data.reduce((sum, item) => sum + item.value, 0);
  const radius = 80;
  const strokeWidth = 12;
  const normalizedRadius = radius - strokeWidth * 2;
  const circumference = normalizedRadius * 2 * Math.PI;

  let cumulativePercentage = 0;

  return (
    <>
      <Card className="shadow-card transition-shadow">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle 
              className="text-lg font-semibold text-foreground cursor-pointer hover:text-primary transition-colors"
              onClick={() => setIsModalOpen(true)}
            >
              Condições Climáticas
            </CardTitle>
            <div className="flex items-center gap-1">
              {onMinimize && (
                <button
                  onClick={onMinimize}
                  className="p-1 rounded hover:bg-muted transition-colors"
                  title={isMinimized ? "Maximizar" : "Minimizar"}
                >
                  {isMinimized ? <ChevronDown className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                </button>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1 rounded hover:bg-muted transition-colors"
                  title="Fechar"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </CardHeader>
        {!isMinimized && total === 0 && (
          <CardContent>
            <div className="h-48 flex flex-col items-center justify-center text-center text-sm text-muted-foreground gap-2">
              <CloudSun className="h-8 w-8 opacity-40" />
              <span>Sem registros de clima ainda. Preencha RDOs para ver as condições.</span>
            </div>
          </CardContent>
        )}
        {!isMinimized && total > 0 && (
          <CardContent className="flex items-center justify-center">
            <div
              className="relative cursor-pointer"
              onClick={() => setIsModalOpen(true)}
            >
              <svg
                height={radius * 2}
                width={radius * 2}
                className="transform -rotate-90"
              >
                {data.map((item, index) => {
                  const percentage = (item.value / total) * 100;
                  const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
                  const strokeDashoffset = -cumulativePercentage * circumference / 100;
                  
                  cumulativePercentage += percentage;

                  return (
                    <circle
                      key={index}
                      stroke={item.color}
                      fill="transparent"
                      strokeWidth={strokeWidth}
                      strokeDasharray={strokeDasharray}
                      strokeDashoffset={strokeDashoffset}
                      r={normalizedRadius}
                      cx={radius}
                      cy={radius}
                      className="transition-all duration-500"
                    />
                  );
                })}
              </svg>
              
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-2xl font-bold text-foreground">{total}</div>
                  <div className="text-sm text-muted-foreground">dias total</div>
                </div>
              </div>
            </div>
            
            <div className="ml-8 space-y-3">
              {data.map((item, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: item.color }}
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground">{item.label}</div>
                    <div className="text-sm text-muted-foreground">{item.value} dias</div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        )}
    </Card>
    
    <WeatherHistoryModal
      open={isModalOpen}
      onOpenChange={setIsModalOpen}
      obraId={obraId}
    />
    </>
  );
}